/* eslint-disable react-hooks/exhaustive-deps */
import { RichTextEditor } from 'components/shared/RichTextEditor/RichTextEditor';
import { TranscriptSkeleton } from 'components/shared/Skeleton/TranscriptSkeleton';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { CustomElement } from 'types/slate-custom-types';
import {
  CHUNK_HEIGHT_EPSILON_PX,
  CHUNK_MAX_BLOCKS,
  CHUNK_LOAD_SCROLL_COOLDOWN_MS,
  CHUNK_MIN_MEASURED_PX,
  CHUNK_MOUNT_DELAY_MS,
  CHUNK_PLACEHOLDER_ROW_PX,
  CHUNK_ROW_PX_MAX,
  CHUNK_ROW_PX_MIN,
  CHUNK_RESUME_WATCHDOG_MS,
  CHUNK_SCROLL_SETTLE_MS,
  CHUNK_SEAM_PEEK_LEAD_PX,
  CHUNK_SEAM_PEEK_ROWS,
  resolveChunkSlotRole,
  resolveScrollTargetChunkId,
  resolveSeamPeekTarget,
  seamPeekAlignForLoadingChunk,
  resolveUpwardSeamSkeletonHeight,
  seamSkeletonRowCount,
  SeamPeekAlign,
  TranscriptBlock,
  TranscriptChunkMeta,
  TRANSCRIPT_DWCM_ENABLED,
} from 'utils/transcriptChunk.util';
import { syncPlaybackHighlightAfterChunkReady } from './playbackHighlight';
import { DwcmChunkedEditorShell } from './DwcmChunkedEditorShell';
import {
  chunkPerfLog,
  transcriptChunkStore,
  TranscriptChunkStoreSnapshot,
} from './TranscriptChunkStore';
import { useTranscriptChunkLoadingCue, resolveTranscriptInteractionLocked, resolveTranscriptLoadingCue } from './useTranscriptChunkLoadingCue';
import { TranscriptViewportLoadingOverlay } from './TranscriptViewportLoadingOverlay';

type Props = {
  fullDocument: any[] | null | undefined;
  videoTimePosition: number;
  /** Media id — binds chunk store to the open video (prevents cross-media reuse). */
  documentKey?: string | null;
};

const estimateHeight = (
  blockCount: number,
  rowPx: number = CHUNK_PLACEHOLDER_ROW_PX,
) => Math.max(blockCount, 1) * rowPx;

const clampRowPx = (px: number) =>
  Math.min(CHUNK_ROW_PX_MAX, Math.max(CHUNK_ROW_PX_MIN, px));

const resolveScrollRoot = (): HTMLElement | null =>
  document.querySelector(
    '[data-transcript-scroll-root]',
  ) as HTMLElement | null;

/** Cumulative estimated top offset of a chunk inside the shell. */
const chunkOffsetTop = (
  index: TranscriptChunkMeta[],
  chunkId: string,
  heightFor: (meta: TranscriptChunkMeta) => number,
): number => {
  let top = 0;
  for (let i = 0; i < index.length; i++) {
    if (index[i].chunkId === chunkId) return top;
    top += heightFor(index[i]);
  }
  return top;
};

const TimedChunkEditor = ({
  chunkId,
  document,
  videoTimePosition,
  setDocument,
  interactionLocked,
}: {
  chunkId: string;
  document: any[];
  videoTimePosition: number;
  setDocument: (nodes: CustomElement[]) => void;
  interactionLocked?: boolean;
}) => {
  // Runs after RichTextEditor has published window.Editor for this chunk.
  useEffect(() => {
    transcriptChunkStore.setEditorBinding(chunkId);
    return () => transcriptChunkStore.clearEditorBinding(chunkId);
  }, [chunkId]);

  useEffect(() => {
    const t0 = performance.now();
    chunkPerfLog('slate:mount-start', {
      chunkId,
      blockCount: document?.length ?? 0,
      elapsedMs: transcriptChunkStore.getLoadElapsedMs(),
    });
    const raf = requestAnimationFrame(() => {
      chunkPerfLog('slate:mount-painted', {
        chunkId,
        blockCount: document?.length ?? 0,
        paintApproxMs: Number((performance.now() - t0).toFixed(1)),
        elapsedMs: transcriptChunkStore.getLoadElapsedMs(),
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [chunkId, document?.length]);

  return (
    <RichTextEditor
      key={chunkId}
      document={document}
      videoTimePosition={videoTimePosition}
      setDocument={setDocument}
      interactionLocked={interactionLocked}
    />
  );
};

/** Same as Player Suspense fallback — never scale rows to chunk height (was 800+ DOM). */
export const ChunkTranscriptSkeleton = ({
  height,
  rows,
  maxRows = 40,
  contentAlign = 'start',
}: {
  height: number;
  rows?: number;
  maxRows?: number;
  contentAlign?: SeamPeekAlign;
}) => {
  const overfill = contentAlign === 'end';
  const rowCount =
    rows ?? seamSkeletonRowCount(height, maxRows, overfill);

  useEffect(() => {
    const t0 = performance.now();
    const raf = requestAnimationFrame(() => {
      chunkPerfLog('skeleton:mounted', {
        height,
        skeletonRows: rowCount,
        paintApproxMs: Number((performance.now() - t0).toFixed(1)),
        elapsedMs: transcriptChunkStore.getLoadElapsedMs(),
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [height, rowCount]);

  return (
    <div
      style={{
        height,
        flexShrink: 0,
        overflow: 'hidden',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: contentAlign === 'end' ? 'flex-end' : 'flex-start',
      }}
      data-chunk-role="loading"
      aria-busy="true"
    >
      <TranscriptSkeleton count={rowCount} />
    </div>
  );
};

/**
 * While scrolling: show only fixed-height placeholders (no Slate, no layout
 * thrash). After settle: load at most one chunk (skeleton → Slate).
 */
/**
 * Router: SPP-675-2 DWCM when enabled + multi-chunk; legacy single-chunk shell otherwise.
 * DWCM is keyed to the current editor document only — caption count must not force DWCM
 * when paragraph mode (or any mode) fits in one chunk.
 */
export type ChunkedEditorShellProps = Props;

export const ChunkedEditorShell = React.memo<Props>(
  ({ fullDocument, videoTimePosition, documentKey }) => {
    const blockCount = Array.isArray(fullDocument) ? fullDocument.length : 0;
    const useDwcm =
      TRANSCRIPT_DWCM_ENABLED && blockCount > CHUNK_MAX_BLOCKS;

    if (useDwcm) {
      return (
        <DwcmChunkedEditorShell
          fullDocument={fullDocument}
          videoTimePosition={videoTimePosition}
          documentKey={documentKey}
        />
      );
    }

    return (
      <LegacyChunkedEditorShell
        fullDocument={fullDocument}
        videoTimePosition={videoTimePosition}
        documentKey={documentKey}
      />
    );
  },
);

/**
 * SPP-655 Phase A — single active chunk + placeholder shell (legacy).
 */
const LegacyChunkedEditorShell = React.memo<Props>(
  ({ fullDocument, videoTimePosition, documentKey }) => {
    const isEditMode = useSelector((state: RootState) => state.toggle.isEditMode);
    const [snapshot, setSnapshot] = useState<TranscriptChunkStoreSnapshot>(() =>
      transcriptChunkStore.getSnapshot(),
    );
    const [editorDocument, setEditorDocument] = useState<any[]>(
      () => (fullDocument as any[]) ?? [],
    );
    const [boundChunkId, setBoundChunkId] = useState<string | null>(null);
    const [allowEditorMount, setAllowEditorMount] = useState(true);
    /** User is mid-scroll / scrollbar drag — freeze heavy UI. */
    const [scrollFrozen, setScrollFrozen] = useState(false);
    /**
     * Viewport-anchored loading cue, held from the first freeze until the
     * chunk's editor has painted. In-slot skeletons sit at the top of a slot
     * that can be tens of thousands of px tall, so after settle they are
     * usually off-screen and the reader just sees a blank page.
     */
    const [scrollLoading, setScrollLoading] = useState(false);
    /** Scroll root height, so the loading cue covers what the reader can see. */
    const [viewportPx, setViewportPx] = useState(0);
    /**
     * Settled preparation target, independent of the mounted editor binding.
     * Survives until Slate is ready so upward loads keep a skeleton in-slot.
     */
    const [preparingChunkId, setPreparingChunkId] = useState<string | null>(
      null,
    );
    /**
     * Neighbor slot to paint as a seam skeleton while the active chunk's end
     * (or start) is in the viewport — cues that the next/prev chunk can load.
     */
    const [peekChunkId, setPeekChunkId] = useState<string | null>(null);
    const [peekAlign, setPeekAlign] = useState<SeamPeekAlign>('start');
    const [peekSkeletonHeight, setPeekSkeletonHeight] = useState(
      CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX,
    );

    const pendingChunkIdRef = useRef<string | null>(null);
    const settleTimerRef = useRef<number>(0);
    const mountTimerRef = useRef<number>(0);
    const cooldownTimerRef = useRef<number>(0);
    const scrollFrozenRef = useRef(false);
    /** Ignore scrollbar events while loading / briefly after mount (prevents bounce). */
    const ignoreScrollLoadRef = useRef(false);
    const indexRef = useRef<TranscriptChunkMeta[]>([]);
    /**
     * Slot heights used for scroll math and placeholders. Seeded from an
     * estimate, then replaced by the chunk's measured height once it has
     * rendered — an estimate far above the real height leaves a blank page at
     * the end of every chunk.
     */
    const slotHeightRef = useRef<Record<string, number>>({});
    /** Per-block height learned from measured chunks, applied to unseen ones. */
    const rowPxRef = useRef<number>(CHUNK_PLACEHOLDER_ROW_PX);
    const measuredChunkIdsRef = useRef<Set<string>>(new Set());
    const [layoutRevision, setLayoutRevision] = useState(0);
    const lastPlaybackSeekSnapRef = useRef<string | null>(null);

    useEffect(() => {
      return transcriptChunkStore.subscribe(() => {
        const next = transcriptChunkStore.getSnapshot();
        setSnapshot((prev) => {
          if (
            prev.activeChunkId === next.activeChunkId &&
            prev.loadingChunkId === next.loadingChunkId &&
            prev.editorReady === next.editorReady &&
            prev.revision === next.revision
          ) {
            return prev;
          }
          return next;
        });
      });
    }, []);

    useEffect(() => {
      if (isEmpty(fullDocument)) {
        transcriptChunkStore.clear();
        setEditorDocument([]);
        setBoundChunkId(null);
        setAllowEditorMount(true);
        setScrollFrozen(false);
        setScrollLoading(false);
        setPreparingChunkId(null);
        setPeekChunkId(null);
        setPeekAlign('start');
        setPeekSkeletonHeight(CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX);
        scrollFrozenRef.current = false;
        slotHeightRef.current = {};
        measuredChunkIdsRef.current.clear();
        rowPxRef.current = CHUNK_PLACEHOLDER_ROW_PX;
        indexRef.current = [];
        return;
      }
      // Saving pushes the same transcript back through Redux as a new array.
      // Re-splitting it would reset the reader to the first chunk, blanking
      // the slot they were on until they scroll.
      if (transcriptChunkStore.matchesDocument(fullDocument, documentKey)) {
        chunkPerfLog('shell:init-skip-unchanged-document', {
          activeChunkId: transcriptChunkStore.getActiveChunkId(),
          blockCount: (fullDocument as TranscriptBlock[]).length,
        });
        const snap = transcriptChunkStore.getSnapshot();
        setSnapshot(snap);
        setBoundChunkId(snap.activeChunkId);
        return;
      }

      if (transcriptChunkStore.isSaveInFlight()) {
        chunkPerfLog('shell:init-skip-save-in-flight');
        return;
      }

      transcriptChunkStore.initFromFullDocument(
        fullDocument as TranscriptBlock[],
        undefined,
        documentKey,
      );
      const snap = transcriptChunkStore.getSnapshot();
      indexRef.current = snap.index;
      slotHeightRef.current = {};
      measuredChunkIdsRef.current.clear();
      rowPxRef.current = CHUNK_PLACEHOLDER_ROW_PX;
      snap.index.forEach((m) => {
        slotHeightRef.current[m.chunkId] = estimateHeight(
          m.blockCount,
          rowPxRef.current,
        );
      });
      setSnapshot(snap);
      setEditorDocument(transcriptChunkStore.getActiveNodes());
      setBoundChunkId(snap.activeChunkId);
      const anchorTime = transcriptChunkStore.consumeModeSwitchAnchorTime();
      if (anchorTime != null) {
        const resolved = transcriptChunkStore.resolveChunkForTime(anchorTime, {
          playing: false,
        });
        if (resolved?.meta?.chunkId) {
          transcriptChunkStore.requestChunkLoad(resolved.meta.chunkId);
          const anchoredSnap = transcriptChunkStore.getSnapshot();
          setSnapshot(anchoredSnap);
          setEditorDocument(transcriptChunkStore.getActiveNodes());
          setBoundChunkId(anchoredSnap.activeChunkId);
        }
      }
      setAllowEditorMount(true);
      setScrollFrozen(false);
      setScrollLoading(false);
      setPreparingChunkId(null);
      setPeekChunkId(null);
      setPeekAlign('start');
      setPeekSkeletonHeight(CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX);
      scrollFrozenRef.current = false;
      ignoreScrollLoadRef.current = false;
      transcriptChunkStore.setScrolling(false);
    }, [fullDocument, documentKey]);

    useEffect(() => {
      return () => {
        if (settleTimerRef.current) window.clearTimeout(settleTimerRef.current);
        if (mountTimerRef.current) window.clearTimeout(mountTimerRef.current);
        if (cooldownTimerRef.current) window.clearTimeout(cooldownTimerRef.current);
        transcriptChunkStore.setScrolling(false);
      };
    }, []);

    const { index, activeChunkId, loadingChunkId, editorReady } = snapshot;
    indexRef.current = index;

    // Prefer store loading id; fall back to settled target while React catches up.
    const playbackSeekTarget = transcriptChunkStore.getPlaybackSeekTarget();
    const preparationTargetId =
      loadingChunkId ??
      preparingChunkId ??
      playbackSeekTarget?.chunkId ??
      null;
    const showLoadingOverlay =
      scrollFrozen || scrollLoading || !!playbackSeekTarget;

    const transcriptInteractionLocked = resolveTranscriptInteractionLocked({
      isEditMode,
      allowEditorMount,
      editorReady,
      chunkLoading: showLoadingOverlay,
      scrollFrozen,
      scrollLoading,
      showLoadingOverlay,
      hasPlaybackSeekTarget: !!playbackSeekTarget,
    });

    useTranscriptChunkLoadingCue(
      resolveTranscriptLoadingCue(
        transcriptInteractionLocked,
        showLoadingOverlay,
      ),
    );

    useEffect(() => {
      if (loadingChunkId) {
        setPreparingChunkId(loadingChunkId);
        return;
      }
      if (editorReady) {
        setPreparingChunkId(null);
      }
    }, [loadingChunkId, editorReady]);

    // Any chunk preparation gets the viewport cue, not just scroll-driven ones.
    // Seeking from the progress bar or a keyword jump lands mid-slot, where the
    // in-slot skeleton sits far above the viewport and the reader sees blank.
    useEffect(() => {
      if (!preparationTargetId) return;
      const root = resolveScrollRoot();
      if (root) setViewportPx(root.clientHeight);
      setScrollLoading(true);
    }, [preparationTargetId]);

    // Drop the viewport cue only once Slate has painted the chunk the reader
    // landed on — markEditorReady fires in the same frame as that paint.
    useEffect(() => {
      if (!scrollLoading || scrollFrozen) return;
      if (loadingChunkId != null || !editorReady || !allowEditorMount) return;
      if (activeChunkId && boundChunkId !== activeChunkId) return;
      setScrollLoading(false);
    }, [
      scrollLoading,
      scrollFrozen,
      loadingChunkId,
      editorReady,
      allowEditorMount,
      activeChunkId,
      boundChunkId,
    ]);

    const multiChunk =
      transcriptChunkStore.isEnabled() && index.length > 1 && !!activeChunkId;

    /** Progress-bar seek: jump viewport to target chunk + skeleton (not blank placeholders). */
    useEffect(() => {
      if (!multiChunk) return;

      const snapPlaybackSeek = () => {
        const target = transcriptChunkStore.getPlaybackSeekTarget();
        if (!target) {
          lastPlaybackSeekSnapRef.current = null;
          return;
        }
        const snapKey = `${target.chunkId}:${target.time.toFixed(3)}`;
        if (lastPlaybackSeekSnapRef.current === snapKey) return;
        lastPlaybackSeekSnapRef.current = snapKey;

        const root = resolveScrollRoot();
        if (!root) return;

        const heightFor = (m: TranscriptChunkMeta) => slotHeight(m);
        const meta = indexRef.current.find((m) => m.chunkId === target.chunkId);
        if (!meta) return;

        const chunkTop = chunkOffsetTop(
          indexRef.current,
          target.chunkId,
          heightFor,
        );
        const chunkH = slotHeight(meta);
        const refId = boundChunkId ?? activeChunkId;
        const align = seamPeekAlignForLoadingChunk(
          indexRef.current,
          target.chunkId,
          refId,
        );
        let scrollTop = chunkTop;
        if (align === 'end') {
          scrollTop = Math.max(0, chunkTop + chunkH - root.clientHeight);
        }

        setScrollLoading(true);
        setViewportPx(root.clientHeight);
        setPreparingChunkId(target.chunkId);
        ignoreScrollLoadRef.current = true;
        transcriptChunkStore.markProgrammaticScroll();
        root.scrollTop = scrollTop;

        chunkPerfLog('shell:playback-seek-snap', {
          chunkId: target.chunkId,
          time: Number(target.time.toFixed(3)),
          align,
          scrollTop: Number(scrollTop.toFixed(1)),
        });
      };

      snapPlaybackSeek();
      return transcriptChunkStore.subscribe(snapPlaybackSeek);
    }, [multiChunk, activeChunkId, boundChunkId]);

    const slotHeight = (meta: TranscriptChunkMeta) =>
      slotHeightRef.current[meta.chunkId] ??
      estimateHeight(meta.blockCount, rowPxRef.current);

    /**
     * When the active chunk's end (or start) enters the viewport, peek the
     * next (or previous) slot as a skeleton so the seam is not a blank page.
     * Uses geometry against reserved slot heights — no DOM layout thrash.
     */
    useEffect(() => {
      if (!multiChunk || !activeChunkId || scrollFrozen) {
        setPeekChunkId(null);
        setPeekAlign('start');
        setPeekSkeletonHeight(CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX);
        return;
      }
      // While a real load is in flight, loading skeleton owns that slot.
      if (preparationTargetId) {
        setPeekChunkId(null);
        setPeekAlign('start');
        setPeekSkeletonHeight(CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX);
        return;
      }

      const root = resolveScrollRoot();
      if (!root) return;

      const updatePeek = () => {
        if (scrollFrozenRef.current || transcriptChunkStore.isLoading()) {
          setPeekChunkId(null);
          setPeekAlign('start');
          setPeekSkeletonHeight(CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX);
          return;
        }
        const heightFor = (m: TranscriptChunkMeta) =>
          slotHeightRef.current[m.chunkId] ??
          estimateHeight(m.blockCount, rowPxRef.current);

        const peek = resolveSeamPeekTarget(
          indexRef.current,
          activeChunkId,
          root.scrollTop,
          root.clientHeight,
          heightFor,
          CHUNK_SEAM_PEEK_LEAD_PX,
        );

        const nextId = peek?.chunkId ?? null;
        const nextAlign = peek?.align ?? 'start';
        const nextSkeletonHeight =
          peek?.skeletonHeight ??
          CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX;
        setPeekChunkId((prev) => (prev === nextId ? prev : nextId));
        setPeekAlign((prev) => (prev === nextAlign ? prev : nextAlign));
        setPeekSkeletonHeight((prev) =>
          prev === nextSkeletonHeight ? prev : nextSkeletonHeight,
        );
      };

      updatePeek();
      root.addEventListener('scroll', updatePeek, { passive: true });
      return () => root.removeEventListener('scroll', updatePeek);
    }, [
      multiChunk,
      activeChunkId,
      scrollFrozen,
      preparationTargetId,
      boundChunkId,
      allowEditorMount,
      layoutRevision,
    ]);

    /**
     * Replace a chunk's reserved height with what it actually renders, and
     * recalibrate the per-block estimate for chunks not rendered yet. Scroll
     * position is compensated so nothing above the viewport shifts.
     */
    const measureChunkSlot = (chunkId: string, reason: string) => {
      const slot = document.querySelector(
        `[data-transcript-chunk="${chunkId}"]`,
      ) as HTMLElement | null;
      const content = slot?.querySelector(
        '[data-slate-editor]',
      ) as HTMLElement | null;
      const measured = Math.round(content?.offsetHeight ?? 0);
      if (measured < CHUNK_MIN_MEASURED_PX) return;

      const previous = slotHeightRef.current[chunkId];
      if (
        previous != null &&
        Math.abs(previous - measured) < CHUNK_HEIGHT_EPSILON_PX
      ) {
        return;
      }

      const heightFor = (m: TranscriptChunkMeta) => slotHeight(m);
      const root = resolveScrollRoot();
      const beforeTop = chunkOffsetTop(indexRef.current, chunkId, heightFor);

      slotHeightRef.current[chunkId] = measured;
      measuredChunkIdsRef.current.add(chunkId);

      const meta = indexRef.current.find((m) => m.chunkId === chunkId);
      const rowPx = clampRowPx(measured / Math.max(meta?.blockCount ?? 1, 1));
      rowPxRef.current = rowPx;
      indexRef.current.forEach((m) => {
        if (measuredChunkIdsRef.current.has(m.chunkId)) return;
        slotHeightRef.current[m.chunkId] = estimateHeight(m.blockCount, rowPx);
      });

      const afterTop = chunkOffsetTop(indexRef.current, chunkId, heightFor);
      if (root && afterTop !== beforeTop) {
        ignoreScrollLoadRef.current = true;
        transcriptChunkStore.markProgrammaticScroll();
        root.scrollTop = Math.max(0, root.scrollTop + (afterTop - beforeTop));
        // Our own scrollTop write must not be read back as a user scroll.
        if (cooldownTimerRef.current) {
          window.clearTimeout(cooldownTimerRef.current);
        }
        cooldownTimerRef.current = window.setTimeout(() => {
          cooldownTimerRef.current = 0;
          ignoreScrollLoadRef.current = false;
        }, CHUNK_LOAD_SCROLL_COOLDOWN_MS);
      }

      chunkPerfLog('shell:measure-slot', {
        chunkId,
        reason,
        estimatedPx: previous ?? null,
        measuredPx: measured,
        rowPx: Number(rowPx.toFixed(1)),
        scrollShiftPx: Number((afterTop - beforeTop).toFixed(1)),
      });
      setLayoutRevision((v) => v + 1);
    };

    /**
     * Preserve the user's scroll position by default.
     * Only adjust when the mounted chunk's content does not intersect the
     * viewport (tall empty slot below a short editor — the blank-screen case).
     */
    const ensureChunkContentVisible = (chunkId: string, reason: string) => {
      const root = resolveScrollRoot();
      if (!root) return;

      // Find next/prev and progress-bar seek position the viewport explicitly.
      if (
        transcriptChunkStore.isFindJumpForChunk(chunkId) ||
        transcriptChunkStore.isPlaybackSeekForChunk(chunkId)
      ) {
        chunkPerfLog('shell:skip-snap-explicit-jump', {
          chunkId,
          reason,
          findJump: transcriptChunkStore.isFindJumpForChunk(chunkId),
          playbackSeek: transcriptChunkStore.isPlaybackSeekForChunk(chunkId),
        });
        return;
      }

      const slot = root.querySelector(
        `[data-transcript-chunk="${chunkId}"]`,
      ) as HTMLElement | null;
      if (!slot) return;

      const rootRect = root.getBoundingClientRect();
      const content =
        (slot.querySelector('[data-slate-editor]') as HTMLElement | null) ??
        slot;
      const contentRect = content.getBoundingClientRect();
      const intersects =
        contentRect.bottom > rootRect.top + 4 &&
        contentRect.top < rootRect.bottom - 4;

      if (intersects) {
        chunkPerfLog('shell:keep-scroll', {
          chunkId,
          reason,
          scrollTop: Number(root.scrollTop.toFixed(1)),
        });
        return;
      }

      const heightFor = (meta: TranscriptChunkMeta) => slotHeight(meta);
      const top = chunkOffsetTop(indexRef.current, chunkId, heightFor);
      const prev = root.scrollTop;
      ignoreScrollLoadRef.current = true;
      transcriptChunkStore.markProgrammaticScroll();
      root.scrollTop = top;
      chunkPerfLog('shell:snap-viewport', {
        chunkId,
        reason,
        from: Number(prev.toFixed(1)),
        to: Number(top.toFixed(1)),
        why: 'content-not-in-viewport',
      });
    };

    // Load started → skeleton, then mount Slate once.
    useEffect(() => {
      if (!multiChunk || !activeChunkId) return;
      if (loadingChunkId !== activeChunkId) return;
      if (scrollFrozenRef.current) {
        chunkPerfLog('shell:load-effect:skipped-frozen', {
          activeChunkId,
          elapsedMs: transcriptChunkStore.getLoadElapsedMs(),
        });
        return;
      }

      const t0 = performance.now();
      setAllowEditorMount(false);
      const nodes = transcriptChunkStore.getChunkNodes(activeChunkId);
      setEditorDocument(nodes);
      setBoundChunkId(activeChunkId);
      const prepareMs = performance.now() - t0;

      chunkPerfLog('shell:prepare-document', {
        chunkId: activeChunkId,
        blockCount: nodes.length,
        prepareMs: Number(prepareMs.toFixed(1)),
        mountDelayMs: CHUNK_MOUNT_DELAY_MS,
        elapsedMs: transcriptChunkStore.getLoadElapsedMs(),
      });

      if (mountTimerRef.current) window.clearTimeout(mountTimerRef.current);
      mountTimerRef.current = window.setTimeout(() => {
        mountTimerRef.current = 0;
        chunkPerfLog('shell:allow-editor-mount', {
          chunkId: activeChunkId,
          blockCount: nodes.length,
          afterDelayMs: CHUNK_MOUNT_DELAY_MS,
          elapsedMs: transcriptChunkStore.getLoadElapsedMs(),
        });
        setAllowEditorMount(true);
      }, CHUNK_MOUNT_DELAY_MS);
    }, [multiChunk, activeChunkId, loadingChunkId]);

    // After Slate is allowed to mount, mark ready once it has painted.
    useEffect(() => {
      if (!multiChunk || !activeChunkId) return;
      if (!allowEditorMount) return;
      // Wait until this chunk is the one we intended to show.
      if (
        loadingChunkId != null &&
        loadingChunkId !== activeChunkId
      ) {
        return;
      }
      if (scrollFrozenRef.current) return;
      // Already ready for this chunk — nothing to do.
      if (
        loadingChunkId == null &&
        transcriptChunkStore.isEditorReady() &&
        boundChunkId === activeChunkId
      ) {
        return;
      }

      const id = activeChunkId;
      const blockCount =
        transcriptChunkStore.getChunkNodes(activeChunkId).length;
      chunkPerfLog('shell:slate-mount-scheduled', {
        chunkId: id,
        blockCount,
        elapsedMs: transcriptChunkStore.getLoadElapsedMs(),
      });

      const paintT0 = performance.now();
      const raf = requestAnimationFrame(() => {
        // Reserved height must match what this chunk really renders, otherwise
        // the leftover slot shows as a blank page after the last block.
        measureChunkSlot(id, 'editor-mounted');
        // Only snap if content is off-screen; otherwise keep scrollbar position.
        ensureChunkContentVisible(id, 'editor-mounted');
        chunkPerfLog('shell:slate-first-paint-raf', {
          chunkId: id,
          blockCount,
          rafWaitMs: Number((performance.now() - paintT0).toFixed(1)),
          elapsedMs: transcriptChunkStore.getLoadElapsedMs(),
        });
        transcriptChunkStore.markEditorReady(id);
        // Layout/scroll events from Slate mount must not bounce to another chunk.
        ignoreScrollLoadRef.current = true;
        if (cooldownTimerRef.current) {
          window.clearTimeout(cooldownTimerRef.current);
        }
        cooldownTimerRef.current = window.setTimeout(() => {
          cooldownTimerRef.current = 0;
          ignoreScrollLoadRef.current = false;
          chunkPerfLog('shell:scroll-cooldown-end', { chunkId: id });
        }, CHUNK_LOAD_SCROLL_COOLDOWN_MS);

        // If playback was paused for this chunk load, land at chunk start + resume.
        const video = window.Video as HTMLMediaElement | undefined;
        if (video?.paused && !video.ended) {
          const meta = transcriptChunkStore.getMeta(id);
          const t = video.currentTime || 0;
          // Check placement before consuming, so a miss here leaves the pending
          // resume for the watchdog instead of burning it.
          const inChunk = transcriptChunkStore.isTimeInChunk(id, t);
          if (!inChunk) {
            chunkPerfLog('shell:skip-resume-playhead-elsewhere', {
              chunkId: id,
              currentTime: Number(t.toFixed(3)),
              chunkStart: meta?.startTime,
              chunkEnd: meta?.endTime,
            });
          } else if (
            !transcriptChunkStore.isFindJumpForChunk(id) &&
            transcriptChunkStore.consumePlaybackResumeForChunk(id)
          ) {
            transcriptChunkStore.clearScrollPinnedChunk();
            // Playback seam: always open at the start of the next chunk
            // (do not keep the previous chunk's end scroll position).
            const root = resolveScrollRoot();
            if (root) {
              const heightFor = (m: TranscriptChunkMeta) => slotHeight(m);
              const top = chunkOffsetTop(indexRef.current, id, heightFor);
              ignoreScrollLoadRef.current = true;
              transcriptChunkStore.markProgrammaticScroll();
              root.scrollTop = top;
              chunkPerfLog('shell:snap-playback-chunk-start', {
                chunkId: id,
                scrollTop: Number(top.toFixed(1)),
                currentTime: Number(t.toFixed(3)),
              });
            }
            // Re-bind highlight to this chunk and scroll current caption into view.
            syncPlaybackHighlightAfterChunkReady();
            chunkPerfLog('shell:auto-resume-playback', {
              chunkId: id,
              currentTime: Number(t.toFixed(3)),
            });
            void video.play()?.catch(() => undefined);
          }
        }
      });
      return () => cancelAnimationFrame(raf);
    }, [allowEditorMount, activeChunkId, loadingChunkId, multiChunk, boundChunkId]);

    // A freeze or remount can swallow the mount frame that hands playback back.
    // Without this the user has to press play again at the chunk seam.
    useEffect(() => {
      if (!multiChunk || !activeChunkId) return;
      if (loadingChunkId != null || !editorReady) return;
      if (!transcriptChunkStore.wasPlaybackPausedForLoad()) return;
      if (transcriptChunkStore.getPlaybackResumeChunkId() !== activeChunkId) {
        return;
      }

      const id = activeChunkId;
      const timer = window.setTimeout(() => {
        const video = window.Video as HTMLMediaElement | undefined;
        if (!video || !video.paused || video.ended) return;
        const t = video.currentTime || 0;
        if (!transcriptChunkStore.isTimeInChunk(id, t)) return;
        if (!transcriptChunkStore.consumePlaybackResumeForChunk(id)) return;

        transcriptChunkStore.clearScrollPinnedChunk();
        syncPlaybackHighlightAfterChunkReady();
        chunkPerfLog('shell:auto-resume-playback-fallback', {
          chunkId: id,
          currentTime: Number(t.toFixed(3)),
        });
        void video.play()?.catch(() => undefined);
      }, CHUNK_RESUME_WATCHDOG_MS);

      return () => window.clearTimeout(timer);
    }, [multiChunk, activeChunkId, loadingChunkId, editorReady]);

    // Scroll: refs only + optional one freeze setState. Settle loads one chunk.
    useEffect(() => {
      if (!multiChunk) return;
      const root = resolveScrollRoot();
      if (!root) return;

      const heightFor = (meta: TranscriptChunkMeta) => slotHeight(meta);

      const enterFreeze = () => {
        if (scrollFrozenRef.current) return;
        const freezeT0 = performance.now();
        const activeId = transcriptChunkStore.getActiveChunkId();
        // Last chance to record the real height before Slate unmounts; rows
        // grow as content-visibility paints them.
        const mountedId = transcriptChunkStore.getEditorChunkId() ?? activeId;
        if (mountedId) measureChunkSlot(mountedId, 'before-freeze');
        // Flush edits back to the chunk the editor holds, not the requested one.
        if (mountedId && window?.Editor?.children) {
          const nodes = window.Editor.children as TranscriptBlock[];
          const existing = transcriptChunkStore.getChunkNodes(mountedId);
          // Avoid silent drop of trailing blocks at chunk boundary unmount.
          if (!existing.length || nodes.length >= existing.length) {
            transcriptChunkStore.flushActiveFromEditor(nodes, 'enter-freeze');
          }
        }
        scrollFrozenRef.current = true;
        transcriptChunkStore.setScrolling(true);
        setScrollFrozen(true);
        setScrollLoading(true);
        setViewportPx(root.clientHeight);
        setAllowEditorMount(false);
        const pending = pendingChunkIdRef.current;
        if (pending) {
          setPreparingChunkId(pending);
        }
        chunkPerfLog('shell:enter-freeze', {
          activeId,
          freezeMs: Number((performance.now() - freezeT0).toFixed(1)),
          settleWaitMs: CHUNK_SCROLL_SETTLE_MS,
        });
      };

      const settle = () => {
        settleTimerRef.current = 0;
        const settleT0 = performance.now();
        const activeId = transcriptChunkStore.getActiveChunkId();
        const target =
          pendingChunkIdRef.current ??
          resolveScrollTargetChunkId(
            indexRef.current,
            root.scrollTop,
            root.clientHeight,
            heightFor,
            activeId,
          );
        pendingChunkIdRef.current = null;

        if (!target) {
          setPreparingChunkId(null);
          scrollFrozenRef.current = false;
          transcriptChunkStore.setScrolling(false);
          setScrollFrozen(false);
          setAllowEditorMount(true);
          chunkPerfLog('shell:settle:no-target', {
            settleMs: Number((performance.now() - settleT0).toFixed(1)),
          });
          return;
        }

        // Same chunk already ready — just remount editor, no store thrash
        if (
          target === transcriptChunkStore.getActiveChunkId() &&
          transcriptChunkStore.isEditorReady()
        ) {
          setPreparingChunkId(null);
          scrollFrozenRef.current = false;
          transcriptChunkStore.setScrolling(false);
          setScrollFrozen(false);
          setEditorDocument(transcriptChunkStore.getChunkNodes(target));
          setBoundChunkId(target);
          setAllowEditorMount(true);
          chunkPerfLog('shell:settle:same-chunk-ready', {
            target,
            settleMs: Number((performance.now() - settleT0).toFixed(1)),
          });
          return;
        }

        // Keep skeleton on target chunk before unfreezing (no blank frame).
        setPreparingChunkId(target);
        scrollFrozenRef.current = false;
        transcriptChunkStore.setScrolling(false);
        setScrollFrozen(false);

        // One load only — ignore further scrollbar events until mount cooldown ends.
        // Keep the user's scroll position (do not jump to chunk start).
        ignoreScrollLoadRef.current = true;
        chunkPerfLog('shell:settle:request-load', {
          target,
          blockCount: transcriptChunkStore.getChunkNodes(target).length,
          settleMs: Number((performance.now() - settleT0).toFixed(1)),
          settleWaitMs: CHUNK_SCROLL_SETTLE_MS,
          scrollTop: Number(root.scrollTop.toFixed(1)),
        });
        // Stop playback yank-back to playhead chunk (usually c0000 at t≈0).
        transcriptChunkStore.setScrollPinnedChunk(target);
        const video = window.Video as HTMLMediaElement | undefined;
        if (video && !video.paused && !video.ended) {
          const t = video.currentTime || 0;
          const playheadInTarget = transcriptChunkStore.isTimeInChunk(
            target,
            t,
          );
          // Crossing into the playhead's next chunk via scroll — resume after mount.
          if (playheadInTarget) {
            transcriptChunkStore.markPlaybackPausedForLoad(target);
          }
          try {
            video.pause();
          } catch {
            // ignore
          }
        }
        transcriptChunkStore.requestChunkLoad(target);
      };

      const onScroll = () => {
        // While loading / post-mount cooldown: keep placeholders stable, no new loads.
        if (ignoreScrollLoadRef.current || transcriptChunkStore.isLoading()) {
          return;
        }
        // Playback follow scrolls the root itself. Treating that as a user
        // scroll pins the chunk and pauses playback at every seam.
        if (transcriptChunkStore.isProgrammaticScroll()) {
          return;
        }

        // Prefer active chunk while it still intersects the viewport so the
        // last words at a chunk seam are not unmounted too early.
        const activeId = transcriptChunkStore.getActiveChunkId();
        const pending = resolveScrollTargetChunkId(
          indexRef.current,
          root.scrollTop,
          root.clientHeight,
          heightFor,
          activeId,
        );
        pendingChunkIdRef.current = pending;

        // Still inside the same chunk — keep Slate mounted; do not freeze.
        if (
          pending &&
          activeId &&
          pending === activeId &&
          !scrollFrozenRef.current
        ) {
          return;
        }

        enterFreeze();

        if (settleTimerRef.current) {
          window.clearTimeout(settleTimerRef.current);
        }
        settleTimerRef.current = window.setTimeout(
          settle,
          CHUNK_SCROLL_SETTLE_MS,
        );
      };

      root.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        root.removeEventListener('scroll', onScroll);
        if (settleTimerRef.current) {
          window.clearTimeout(settleTimerRef.current);
        }
        transcriptChunkStore.setScrolling(false);
      };
    }, [multiChunk, index.length]);

    const handleSetDocument = (nodes: CustomElement[]) => {
      setEditorDocument(nodes);
      // Write to the chunk this editor was mounted with. activeChunkId already
      // points at the next chunk while a load is in flight, so keying on it
      // would overwrite the incoming chunk with the outgoing one's blocks.
      const id =
        transcriptChunkStore.getEditorChunkId() ??
        transcriptChunkStore.getActiveChunkId();
      if (id) {
        transcriptChunkStore.updateChunk(
          id,
          nodes as TranscriptBlock[],
          'editor-change',
        );
      }
    };

    if (!multiChunk) {
      return (
        <RichTextEditor
          document={editorDocument ?? []}
          videoTimePosition={videoTimePosition}
          setDocument={handleSetDocument}
        />
      );
    }

    const overlayHeight =
      viewportPx || CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX;

    const loadingSkeletonAlign = seamPeekAlignForLoadingChunk(
      index,
      preparationTargetId,
      boundChunkId ?? activeChunkId,
    );

    const resolveSeamSkeletonHeight = (
      chunkId: string,
      slotHeightPx: number,
      align: SeamPeekAlign,
      peekHeight: number,
    ) => {
      if (align === 'end' && activeChunkId) {
        const root = resolveScrollRoot();
        if (root) {
          const heightFor = (m: TranscriptChunkMeta) => slotHeight(m);
          return resolveUpwardSeamSkeletonHeight(
            index,
            chunkId,
            activeChunkId,
            root.scrollTop,
            heightFor,
          );
        }
      }
      return Math.min(slotHeightPx, peekHeight);
    };

    const defaultSeamSkeletonHeight =
      CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX;

    // Mount Slate as soon as allowEditorMount — do not wait for markEditorReady
    // (that previously kept the editor hidden until after ready cleared loading).
    // Skeleton keys off preparationTargetId (loadingChunkId), not showEditor.
    return (
      <>
      <div
        data-transcript-chunked-shell="true"
        data-chunk-scroll-frozen={scrollFrozen ? '1' : '0'}
      >
        {index.map((meta) => {
          const h = slotHeight(meta);
          const role = resolveChunkSlotRole({
            chunkId: meta.chunkId,
            activeChunkId,
            boundChunkId,
            loadingChunkId: preparationTargetId,
            peekChunkId,
            scrollFrozen,
            allowEditorMount,
          });

          if (role === 'loading' || role === 'peek') {
            const skeletonAlign = role === 'loading' ? loadingSkeletonAlign : peekAlign;
            const skeletonHeight =
              skeletonAlign === 'end'
                ? resolveSeamSkeletonHeight(
                    meta.chunkId,
                    h,
                    skeletonAlign,
                    role === 'peek'
                      ? peekSkeletonHeight
                      : defaultSeamSkeletonHeight,
                  )
                : Math.min(
                    h,
                    role === 'peek'
                      ? peekSkeletonHeight
                      : defaultSeamSkeletonHeight,
                  );
            return (
              <div
                key={meta.chunkId}
                data-transcript-chunk={meta.chunkId}
                data-chunk-role={role}
                style={{
                  height: h,
                  flexShrink: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent:
                    skeletonAlign === 'end' ? 'flex-end' : 'flex-start',
                  background: '#fff',
                }}
                aria-busy={role === 'loading' || role === 'peek' ? true : undefined}
                aria-hidden={role === 'peek' ? true : undefined}
              >
                <ChunkTranscriptSkeleton
                  height={skeletonHeight}
                  maxRows={role === 'loading' ? 50 : 40}
                  contentAlign={skeletonAlign}
                />
              </div>
            );
          }

          if (role === 'active') {
            return (
              <div
                key={meta.chunkId}
                data-transcript-chunk={meta.chunkId}
                data-chunk-role="active"
                // No reserved height: the editor's own height is the truth.
                // Padding the slot out to an estimate renders as a blank page
                // between the last block of this chunk and the next chunk.
                style={{ flexShrink: 0 }}
              >
                <TimedChunkEditor
                  chunkId={meta.chunkId}
                  document={editorDocument ?? []}
                  videoTimePosition={videoTimePosition}
                  setDocument={handleSetDocument}
                  interactionLocked={transcriptInteractionLocked}
                />
              </div>
            );
          }

          // Scrollbar drag / inactive: empty height reserve only (cheap).
          return (
            <div
              key={meta.chunkId}
              data-transcript-chunk={meta.chunkId}
              data-chunk-role="placeholder"
              aria-hidden
              style={{ height: h, flexShrink: 0 }}
            />
          );
        })}
      </div>
      <TranscriptViewportLoadingOverlay active={showLoadingOverlay} />
      </>
    );
  },
);

LegacyChunkedEditorShell.displayName = 'LegacyChunkedEditorShell';
ChunkedEditorShell.displayName = 'ChunkedEditorShell';
